/*
 * PDP variant debug HUD — opt-in, off unless the URL carries ?pdpdebug=1
 *
 * Diagnostic tool for the "Unavailable after a colour switch, then a size
 * change" report (client, 2026-08). The failure is intermittent and does not
 * reproduce reliably in the headless browser pane, which cannot drive iOS
 * Safari's timing; this makes the customer's own device the instrument. The
 * same play as the Origins timeline's ?otdebug=1 HUD, which settled six
 * rounds of inference in one screenshot.
 *
 * It only wraps and reports — it changes no behaviour. The wrappers are
 * installed on the prototype and on window.publish, both of which survive the
 * <main> replacement a colour swap performs, so one HUD spans the whole
 * journey.
 *
 * Read the log bottom-up as a story:
 *   pub   — a selection was published. svid is the variant VariantSelects
 *           resolved. `svid:null` on a size tap means the picker failed to
 *           resolve a combination it should have, and product-info never gets
 *           a chance.
 *   req   — the fetch product-info made. `cache:HIT` means it answered from
 *           __variantSectionCache rather than the network.
 *   resp  — what came back. `sv:null` is the smoking gun for a bad render:
 *           product-info treats it as "no such variant".
 *   swap  — a cross-product (colour) swap landed.
 *   UNAVAIL / SELECTSIZE — which branch set the button, and from where.
 */
(function () {
  if (!/[?&]pdpdebug=1/.test(window.location.search)) return;
  if (window.__pdpDebugInstalled) return;
  window.__pdpDebugInstalled = true;

  var lines = [];
  var box;

  function paint() {
    if (!box) {
      box = document.createElement('div');
      box.id = 'PdpVariantDebug';
      box.setAttribute('style', [
        'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
        'max-height:45vh', 'overflow:auto', 'background:rgba(0,0,0,0.88)',
        'color:#0f0', 'font:10px/1.35 ui-monospace,Menlo,monospace',
        'padding:6px 8px', 'white-space:pre-wrap', 'word-break:break-all',
        'pointer-events:auto'
      ].join(';'));
      box.addEventListener('click', function () { lines.length = 0; paint(); });
      (document.body || document.documentElement).appendChild(box);
    }
    box.textContent = lines.slice(-22).join('\n') + '\n[tap to clear]';
    box.scrollTop = box.scrollHeight;
  }

  var t0 = Date.now();
  function log(text) {
    lines.push(('' + (Date.now() - t0)).padStart(6, ' ') + 'ms ' + text);
    paint();
  }

  function short(url) {
    return String(url || '')
      .replace('/products/', '')
      .replace('section_id=', 'sid=')
      .replace(/template--\d+__/, '');
  }

  function install() {
    var ctor = window.customElements && customElements.get('product-info');
    if (!ctor || !window.publish) return false;
    var proto = ctor.prototype;

    var origPublish = window.publish;
    window.publish = function (name, payload) {
      if (name && String(name).indexOf('option') !== -1) {
        var d = (payload && payload.data) || {};
        log(
          'pub  svid:' + (d.selectedVariantId == null ? 'NULL' : d.selectedVariantId) +
          ' ovs:' + ((d.selectedOptionValues || []).length) +
          ' from:' + (d.target && d.target.tagName)
        );
      }
      return origPublish.apply(this, arguments);
    };

    var origRender = proto.renderProductInfo;
    proto.renderProductInfo = function (opts) {
      var c = window.__variantSectionCache;
      var hit = !!(c && typeof c.has === 'function' && c.has(opts.requestUrl));
      log('req  ' + short(opts.requestUrl) + ' cache:' + (hit ? 'HIT' : 'miss'));
      return origRender.call(this, opts);
    };

    var origUpdate = proto.handleUpdateProductInfo;
    proto.handleUpdateProductInfo = function (url) {
      var inner = origUpdate.call(this, url);
      var self = this;
      return function (html) {
        var node = html.querySelector('variant-selects [data-selected-variant]');
        var raw = node ? String(node.innerHTML).trim() : '';
        var id = 'NO-NODE';
        if (node) {
          try { var p = JSON.parse(raw); id = p && p.id ? p.id : 'null'; } catch (e) { id = 'PARSE-ERR'; }
        }
        log('resp sv:' + id + ' incomplete:' + self.hasIncompleteOptionSelection());
        return inner(html);
      };
    };

    var origSwap = proto.handleSwapProduct;
    proto.handleSwapProduct = function (url, full) {
      var inner = origSwap.call(this, url, full);
      return function (html) {
        log('swap ' + short(url) + ' full:' + full + ' gotPI:' + !!html.querySelector('product-info'));
        return inner(html);
      };
    };

    var origUnavail = proto.setUnavailable;
    proto.setUnavailable = function () {
      log('>>>> UNAVAILABLE  sel:' + selection(this));
      return origUnavail.call(this);
    };

    var origPrompt = proto.setSelectSizePrompt;
    proto.setSelectSizePrompt = function () {
      log('>>>> SELECT-SIZE  sel:' + selection(this));
      return origPrompt.call(this);
    };

    return true;
  }

  function selection(productInfo) {
    var picker = productInfo.querySelector('variant-selects');
    if (!picker) return 'no-picker';
    return Array.prototype.map
      .call(picker.querySelectorAll('[data-option-name]'), function (group) {
        var checked = group.querySelector('input:checked');
        return group.dataset.optionName + '=' + (checked ? checked.value : '-');
      })
      .join(' ');
  }

  // product-info.js and pubsub.js are deferred, so poll briefly until both exist.
  var tries = 0;
  (function attempt() {
    if (install()) {
      log('HUD ready — reproduce now');
      return;
    }
    if (++tries > 100) return;
    window.setTimeout(attempt, 100);
  })();
})();
